import type { FrontendAccessPolicy, PlatformEntryContext, User } from '@/types';
import { normalizeFrontendRole } from './access';

export type AuthBootstrapSnapshot = {
  version: 1;
  savedAtMs: number;
  user: User;
  accessPolicy: FrontendAccessPolicy | null;
  entryContext: PlatformEntryContext | null;
  accessEnvelopeFingerprint: string;
};

export const AUTH_BOOTSTRAP_SNAPSHOT_TTL_MS = 24 * 60 * 60 * 1000;

function stableEnvelopeValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stableEnvelopeValue);
  if (!value || typeof value !== 'object') return value;
  return Object.fromEntries(
    Object.entries(value as Record<string, unknown>)
      .filter(([, item]) => item !== undefined)
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, item]) => [key, stableEnvelopeValue(item)]),
  );
}

/**
 * Canonical client-side comparison key for state that can expose or retain
 * protected data. It is only a revocation/remount signal; the server remains
 * the authorization authority.
 */
export function buildAccessEnvelopeFingerprint(
  user: User | null | undefined,
  accessPolicy: FrontendAccessPolicy | null | undefined,
  entryContext: PlatformEntryContext | null | undefined,
): string {
  return JSON.stringify(stableEnvelopeValue({
    user: user ? {
      id: user.id,
      tenant_id: user.tenant_id ?? null,
      role: user.role,
      enabled: user.enabled,
      access_mode: user.access_mode ?? null,
      access_status: user.access_status ?? null,
      must_change_password: user.must_change_password ?? false,
    } : null,
    accessPolicy: accessPolicy ?? null,
    entryContext: entryContext ?? null,
  }));
}

export function getAuthBootstrapStorageKey(host: string): string {
  const normalized = String(host || '').trim().toLowerCase() || 'default';
  return `mg_auth_bootstrap_v1:${normalized}`;
}

export function buildAuthBootstrapSnapshot(
  user: User,
  accessPolicy: FrontendAccessPolicy | null,
  nowMs = Date.now(),
  entryContext: PlatformEntryContext | null = null,
): AuthBootstrapSnapshot {
  return {
    version: 1,
    savedAtMs: nowMs,
    user,
    accessPolicy,
    entryContext,
    accessEnvelopeFingerprint: buildAccessEnvelopeFingerprint(user, accessPolicy, entryContext),
  };
}

function readPlatformEntryContext(value: unknown): PlatformEntryContext | null {
  if (!value || typeof value !== 'object') return null;
  const candidate = value as Partial<PlatformEntryContext>;
  if (!['pending', 'single_contour', 'multi_contour', 'root_admin'].includes(String(candidate.entry_kind || ''))) {
    return null;
  }
  if (
    !Array.isArray(candidate.contours)
    || typeof candidate.can_switch_all !== 'boolean'
    || (candidate.is_platform_owner !== undefined && typeof candidate.is_platform_owner !== 'boolean')
  ) return null;
  const contours = candidate.contours
    .filter((item): item is PlatformEntryContext['contours'][number] => Boolean(
      item
      && typeof item === 'object'
      && typeof item.tenant_id === 'string'
      && typeof item.label === 'string'
      && typeof item.host === 'string',
    ))
    .map((item) => ({
      tenant_id: item.tenant_id,
      label: item.label,
      host: item.host,
    }));
  if (contours.length !== candidate.contours.length) return null;
  const defaultContour = candidate.default_contour == null
    ? null
    : typeof candidate.default_contour === 'string'
      ? candidate.default_contour
      : null;
  if (candidate.default_contour != null && defaultContour == null) return null;
  const workspaceKinds = new Set(['control', 'city', 'region', 'vertical', 'uk_portfolio', 'management_company']);
  const readWorkspace = (workspace: unknown): NonNullable<PlatformEntryContext['current_workspace']> | null => {
    if (!workspace || typeof workspace !== 'object') return null;
    const item = workspace as Record<string, unknown>;
    if (
      typeof item.key !== 'string'
      || !workspaceKinds.has(String(item.kind || ''))
      || typeof item.label !== 'string'
      || typeof item.description !== 'string'
      || typeof item.host !== 'string'
      || typeof item.path !== 'string'
      || !Array.isArray(item.tenant_ids)
      || item.tenant_ids.some((tenantId) => typeof tenantId !== 'string')
    ) {
      return null;
    }
    const hasManagementCompanyId = Object.prototype.hasOwnProperty.call(item, 'management_company_id');
    const managementCompanyId = item.management_company_id;
    if (
      hasManagementCompanyId
      && managementCompanyId != null
      && (!Number.isInteger(managementCompanyId) || Number(managementCompanyId) <= 0)
    ) {
      return null;
    }
    return {
      key: item.key,
      kind: item.kind as NonNullable<PlatformEntryContext['current_workspace']>['kind'],
      label: item.label,
      description: item.description,
      host: item.host,
      path: item.path,
      tenant_ids: [...item.tenant_ids] as string[],
      ...(hasManagementCompanyId
        ? { management_company_id: managementCompanyId == null ? null : Number(managementCompanyId) }
        : {}),
    };
  };
  let workspaces: PlatformEntryContext['workspaces'];
  if (candidate.workspaces !== undefined) {
    if (!Array.isArray(candidate.workspaces)) return null;
    const parsed = candidate.workspaces.map(readWorkspace);
    if (parsed.some((workspace) => workspace == null)) return null;
    workspaces = parsed as NonNullable<PlatformEntryContext['workspaces']>;
  }
  let defaultWorkspace: string | null | undefined;
  if (candidate.default_workspace !== undefined) {
    if (candidate.default_workspace !== null && typeof candidate.default_workspace !== 'string') return null;
    defaultWorkspace = candidate.default_workspace;
  }
  let currentWorkspace: PlatformEntryContext['current_workspace'];
  if (candidate.current_workspace !== undefined) {
    if (candidate.current_workspace === null) currentWorkspace = null;
    else {
      currentWorkspace = readWorkspace(candidate.current_workspace);
      if (!currentWorkspace) return null;
    }
  }
  return {
    entry_kind: candidate.entry_kind as PlatformEntryContext['entry_kind'],
    // Browser persistence is a navigation-performance hint, never authority
    // for the owner-only module. A stored true value intentionally normalizes
    // to false, invalidating its envelope fingerprint and forcing fresh server
    // entry-context proof before /owner/assistant can mount.
    is_platform_owner: false,
    contours,
    default_contour: defaultContour,
    ...(candidate.workspaces !== undefined ? { workspaces } : {}),
    ...(candidate.default_workspace !== undefined ? { default_workspace: defaultWorkspace } : {}),
    ...(candidate.current_workspace !== undefined ? { current_workspace: currentWorkspace } : {}),
    can_switch_all: candidate.can_switch_all,
  };
}

type JwtBootstrapClaims = {
  userId: number | null;
  role: string | null;
  expMs: number | null;
  accessMode: 'legacy' | 'explicit' | null;
  accessStatus: 'pending' | 'active' | 'suspended' | null;
};

function decodeBase64UrlSegment(segment: string): string | null {
  const normalized = segment.replace(/-/g, '+').replace(/_/g, '/');
  const padded = normalized + '='.repeat((4 - (normalized.length % 4 || 4)) % 4);
  try {
    if (typeof atob === 'function') {
      return atob(padded);
    }
  } catch {
    // ignore
  }
  return null;
}

export function parseJwtBootstrapClaims(token: string | null | undefined): JwtBootstrapClaims | null {
  const raw = String(token || '').trim();
  if (!raw) return null;
  const parts = raw.split('.');
  if (parts.length < 2) return null;
  const decoded = decodeBase64UrlSegment(parts[1]);
  if (!decoded) return null;
  try {
    const payload = JSON.parse(decoded) as Record<string, unknown>;
    const rawUserId = payload.user_id ?? payload.sub;
    const userId =
      typeof rawUserId === 'number'
        ? Math.trunc(rawUserId)
        : typeof rawUserId === 'string' && rawUserId.trim().match(/^\d+$/)
          ? Number.parseInt(rawUserId.trim(), 10)
          : null;
    const role = typeof payload.role === 'string' && payload.role.trim() ? payload.role.trim().toLowerCase() : null;
    const expMs =
      typeof payload.exp === 'number' && Number.isFinite(payload.exp)
        ? Math.trunc(payload.exp * 1000)
        : typeof payload.exp === 'string' && Number.isFinite(Number(payload.exp))
          ? Math.trunc(Number(payload.exp) * 1000)
          : null;
    const accessMode = payload.access_mode === 'explicit' || payload.access_mode === 'legacy'
      ? payload.access_mode
      : null;
    const accessStatus = payload.access_status === 'pending'
      || payload.access_status === 'active'
      || payload.access_status === 'suspended'
      ? payload.access_status
      : null;
    return { userId, role, expMs, accessMode, accessStatus };
  } catch {
    return null;
  }
}

export function buildJwtBootstrapFallbackUser(
  token: string | null | undefined,
  nowMs = Date.now(),
): User | null {
  const claims = parseJwtBootstrapClaims(token);
  if (!claims?.userId) return null;
  if (claims.expMs != null && claims.expMs <= nowMs + 5_000) {
    return null;
  }
  const role = normalizeFrontendRole(claims.role);
  if (!role) return null;
  const nowIso = new Date(nowMs).toISOString();
  return {
    id: claims.userId,
    username: `user_${claims.userId}`,
    first_name: '',
    last_name: '',
    email: `user_${claims.userId}@bootstrap.local`,
    role,
    ...(claims.accessMode ? { access_mode: claims.accessMode } : {}),
    ...(claims.accessStatus ? { access_status: claims.accessStatus } : {}),
    enabled: true,
    created_at: nowIso,
    updated_at: nowIso,
  };
}

export function readUsableAuthBootstrapSnapshot(
  rawSnapshot: string | null | undefined,
  token: string | null | undefined,
  nowMs = Date.now(),
): AuthBootstrapSnapshot | null {
  if (!rawSnapshot) return null;
  const claims = parseJwtBootstrapClaims(token);
  if (claims?.expMs != null && claims.expMs <= nowMs + 5_000) {
    return null;
  }
  try {
    const parsed = JSON.parse(rawSnapshot) as Partial<AuthBootstrapSnapshot> & {
      access_policy?: FrontendAccessPolicy | null;
    };
    const savedAtMs = Number(parsed.savedAtMs);
    if (!Number.isFinite(savedAtMs)) return null;
    if (nowMs - savedAtMs > AUTH_BOOTSTRAP_SNAPSHOT_TTL_MS) return null;
    const user = parsed.user;
    if (!user || typeof user !== 'object') return null;
    const userId = Number((user as { id?: unknown }).id);
    const userRole = String((user as { role?: unknown }).role || '').trim().toLowerCase();
    if (!Number.isFinite(userId) || !userRole) return null;
    if (claims?.userId != null && claims.userId !== userId) return null;
    if (claims?.role != null && claims.role !== userRole) return null;
    const accessPolicy = parsed.accessPolicy ?? parsed.access_policy ?? null;
    const entryContext = readPlatformEntryContext(parsed.entryContext);
    const accessEnvelopeFingerprint = buildAccessEnvelopeFingerprint(
      user as User,
      accessPolicy as FrontendAccessPolicy | null,
      entryContext,
    );
    if (
      typeof parsed.accessEnvelopeFingerprint === 'string'
      && parsed.accessEnvelopeFingerprint !== accessEnvelopeFingerprint
    ) {
      return null;
    }
    return {
      version: 1,
      savedAtMs,
      user: user as User,
      accessPolicy: accessPolicy as FrontendAccessPolicy | null,
      entryContext,
      accessEnvelopeFingerprint,
    };
  } catch {
    return null;
  }
}
